import React, { useEffect, useState } from 'react';
import { Table, Button, Space, Typography, Tag, Modal, Form, Input, InputNumber, Select, Spin, message } from 'antd';
import { PlusOutlined, DownloadOutlined } from '@ant-design/icons';
import { apiClient as api } from '../../../shared/api/axios-instance';

const { Title } = Typography;
const { Option } = Select;

export const AdjustmentsScreen: React.FC = () => {
    const [data, setData] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 15, total: 0 });
    
    // Modal states
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [form] = Form.useForm();
    const [merchantResults, setMerchantResults] = useState<any[]>([]);
    const [searching, setSearching] = useState(false);

    const fetchAdjustments = async (page = 1, pageSize = 15) => {
        try {
            setLoading(true);
            const response = await api.get(`/admin/financials/adjustments?page=${page}&per_page=${pageSize}`);
            setData(response.data.data.data);
            setPagination({
                current: response.data.data.current_page,
                pageSize: response.data.data.per_page,
                total: response.data.data.total,
            });
        } catch (error) {
            message.error('Failed to load adjustments');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAdjustments();
    }, []);

    const handleTableChange = (newPagination: any) => {
        fetchAdjustments(newPagination.current, newPagination.pageSize);
    };

    const handleSearchMerchant = async (value: string) => {
        if (!value) {
            setMerchantResults([]);
            return;
        }
        try {
            setSearching(true);
            const response = await api.get(`/admin/financials/merchants/search?q=${value}`);
            setMerchantResults(response.data.data);
        } catch (error) {
            message.error('Failed to search merchant');
        } finally {
            setSearching(false);
        }
    };

    const handleCreateAdjustment = async (values: any) => {
        try {
            setSubmitting(true);
            await api.post('/admin/financials/adjustments', values);
            message.success('Adjustment executed successfully');
            setIsModalVisible(false);
            form.resetFields();
            fetchAdjustments(1); // Refresh data
        } catch (error: any) {
            message.error(error.response?.data?.message || 'Failed to create adjustment');
        } finally {
            setSubmitting(false);
        }
    };

    const exportToCsv = () => {
        // Simple client-side export for demo
        const headers = ['ID', 'Reference Number', 'Amount', 'Type', 'Reason', 'Notes', 'Date'];
        const csvRows = [headers.join(',')];
        data.forEach(row => {
            csvRows.push([
                row.id,
                row.reference_number,
                row.amount,
                row.type,
                row.reason,
                `"${row.notes || ''}"`,
                row.created_at
            ].join(','));
        });
        const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `adjustments_${new Date().getTime()}.csv`;
        a.click();
    };

    const columns = [
        { title: 'ID', dataIndex: 'id', key: 'id' },
        { title: 'Ref Number', dataIndex: 'reference_number', key: 'reference_number' },
        { title: 'Merchant ID', dataIndex: 'wallet', key: 'merchant', render: (w: any) => w?.merchant_id },
        { 
            title: 'Type', 
            dataIndex: 'type', 
            key: 'type',
            render: (type: string) => (
                <Tag color={type === 'adjustment_credit' ? 'success' : 'error'}>
                    {type.toUpperCase()}
                </Tag>
            )
        },
        { title: 'Amount', dataIndex: 'amount', key: 'amount' },
        { title: 'Reason', dataIndex: 'reason', key: 'reason' },
        { title: 'Approved By', dataIndex: 'approved_by', key: 'approved_by' },
        { title: 'Date', dataIndex: 'created_at', key: 'created_at' },
    ];

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <Title level={2} style={{ margin: 0 }}>Wallet Adjustments</Title>
                <Space>
                    <Button icon={<DownloadOutlined />} onClick={exportToCsv}>Export CSV</Button>
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => setIsModalVisible(true)}>
                        New Adjustment
                    </Button>
                </Space>
            </div>

            <Table 
                columns={columns} 
                dataSource={data} 
                rowKey="id" 
                loading={loading}
                pagination={pagination}
                onChange={handleTableChange}
            />

            <Modal
                title="Create Manual Adjustment"
                open={isModalVisible}
                onCancel={() => setIsModalVisible(false)}
                footer={null}
            >
                <Form layout="vertical" form={form} onFinish={handleCreateAdjustment}>
                    <Form.Item 
                        name="merchant_id" 
                        label="Merchant" 
                        rules={[{ required: true, message: 'Please select a merchant' }]}
                    >
                        <Select
                            showSearch
                            placeholder="Search merchant by ID, Email, or Phone"
                            defaultActiveFirstOption={false}
                            showArrow={false}
                            filterOption={false}
                            onSearch={handleSearchMerchant}
                            notFoundContent={searching ? <Spin size="small" /> : null}
                            options={(merchantResults || []).map(d => ({
                                value: d.id,
                                label: `${d.name} (ID: ${d.id}) - Bal: ${d.balance}`,
                            }))}
                        />
                    </Form.Item>

                    <Form.Item 
                        name="direction" 
                        label="Direction" 
                        rules={[{ required: true, message: 'Please select direction' }]}
                    >
                        <Select placeholder="Select Credit or Debit">
                            <Option value="credit">Credit (+)</Option>
                            <Option value="debit">Debit (-)</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item 
                        name="amount" 
                        label="Amount" 
                        rules={[{ required: true, message: 'Please enter amount' }]}
                    >
                        <InputNumber min={0.01} style={{ width: '100%' }} />
                    </Form.Item>

                    <Form.Item 
                        name="reason" 
                        label="Reason" 
                        rules={[{ required: true, message: 'Please select reason' }]}
                    >
                        <Select placeholder="Select Reason">
                            <Option value="refund">Refund</Option>
                            <Option value="bonus">Bonus</Option>
                            <Option value="correction">Correction</Option>
                            <Option value="promotion">Promotion</Option>
                            <Option value="support">Support</Option>
                            <Option value="migration">Migration</Option>
                            <Option value="other">Other</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item name="notes" label="Notes">
                        <Input.TextArea rows={3} placeholder="Provide audit trail details..." />
                    </Form.Item>

                    <Form.Item>
                        <Button type="primary" htmlType="submit" loading={submitting} block>
                            Execute Adjustment
                        </Button>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};
